<template>
  <div class="modal-backdrop fade show" v-if="modelValue" @click.self="close"></div>
  <div class="modal d-block" tabindex="-1" v-if="modelValue">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content topup-modal">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-wallet2 me-2"></i>
            {{ $t('subscription.topup_balance') }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Current Balance -->
          <div class="current-balance-card">
            <span class="balance-label">{{ $t('subscription.current_balance') }}</span>
            <span class="balance-value">{{ formattedBalance }} {{ $t('subscription.currency') }}</span>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">{{ $t('subscription.loading') }}</p>
          </div>

          <!-- Options Grid -->
          <div v-else class="topup-options">
            <div
              v-for="option in topupOptions"
              :key="option.key"
              class="topup-option"
              :class="{ 'has-bonus': option.bonus > 0 }"
              @click="selectOption(option)"
            >
              <div class="option-amount">{{ option.amount }} ₽</div>
              <div class="option-bonus" v-if="option.bonus > 0">
                +{{ option.bonus }} ₽ {{ $t('subscription.bonus') }}
              </div>
              <div class="option-total" v-if="option.bonus > 0">
                = {{ option.total }} ₽
              </div>
              <a :href="option.payment_url" target="_blank" class="btn btn-primary btn-sm mt-2" @click.stop="startPolling()">
                {{ $t('subscription.pay') }}
              </a>
            </div>
          </div>

          <!-- Payment awaiting -->
          <div v-if="awaitingPayment" class="payment-awaiting mt-3">
            <div class="d-flex align-items-center justify-content-center">
              <div class="spinner-border spinner-border-sm text-success me-2" role="status"></div>
              <span>{{ $t('subscription.awaiting_payment') }}</span>
            </div>
          </div>

          <!-- Payment success -->
          <div v-if="paymentSuccess" class="payment-success mt-3">
            <i class="bi bi-check-circle-fill me-1"></i>
            {{ $t('subscription.payment_success') }}
          </div>

          <!-- Info -->
          <div v-if="!awaitingPayment && !paymentSuccess" class="topup-info mt-3">
            <i class="bi bi-info-circle me-1"></i>
            {{ $t('subscription.topup_info') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';

export default {
  name: 'TopupModal',

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    chatbotName: {
      type: String,
      default: 'meetscribe'
    }
  },

  emits: ['update:modelValue', 'payment-success'],

  data() {
    return {
      awaitingPayment: false,
      paymentSuccess: false,
      pollTimer: null,
      initialBalance: null,
      pollCount: 0,
      polledBalance: null
    };
  },

  computed: {
    ...mapGetters('subscription', ['getBalance', 'getTopupOptions', 'isLoading']),

    loading() {
      return this.isLoading && !this.awaitingPayment;
    },

    topupOptions() {
      return this.getTopupOptions;
    },

    formattedBalance() {
      const balance = this.polledBalance !== null ? this.polledBalance : (this.getBalance || 0);
      return balance.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadOptions();
        this.awaitingPayment = false;
        this.paymentSuccess = false;
        this.polledBalance = null;
      } else {
        this.stopPolling();
      }
    }
  },

  beforeUnmount() {
    this.stopPolling();
  },

  methods: {
    ...mapActions('subscription', ['fetchTopupOptions', 'fetchSubscriptionStatus']),

    close() {
      this.stopPolling();
      this.$emit('update:modelValue', false);
    },

    loadOptions() {
      this.fetchTopupOptions(this.chatbotName);
    },

    selectOption(option) {
      window.open(option.payment_url, '_blank');
      this.startPolling();
    },

    startPolling() {
      this.stopPolling();
      this.initialBalance = this.getBalance || 0;
      this.awaitingPayment = true;
      this.paymentSuccess = false;
      this.pollCount = 0;
      console.log('[TopupModal] Polling started, initial balance:', this.initialBalance);
      this.scheduleNextPoll();
    },

    scheduleNextPoll() {
      this.pollTimer = setTimeout(() => this.checkBalance(), 5000);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      this.awaitingPayment = false;
    },

    async checkBalance() {
      this.pollCount++;
      if (this.pollCount > 120) {
        console.log('[TopupModal] Polling stopped: max attempts reached');
        this.stopPolling();
        return;
      }

      try {
        // Silent fetch — don't use Vuex action to avoid loading state flicker
        const response = await axiosConfig.get(API_URL, {
          params: {
            action: 'get_subscription_status',
            chatbot_name: this.chatbotName
          }
        });

        const data = response.data?.data || response.data?.subscription || {};
        const newBalance = data.balance || 0;
        this.polledBalance = newBalance;
        console.log('[TopupModal] Poll #' + this.pollCount + ', balance:', newBalance, '(initial:', this.initialBalance + ')');

        if (newBalance > this.initialBalance) {
          console.log('[TopupModal] Payment detected!');
          this.stopPolling();
          this.awaitingPayment = false;
          this.paymentSuccess = true;
          // Update Vuex store with new balance
          this.fetchSubscriptionStatus(this.chatbotName);
          this.$emit('payment-success');
          setTimeout(() => {
            this.paymentSuccess = false;
            this.close();
          }, 3000);
          return;
        }
      } catch (e) {
        console.log('[TopupModal] Poll error:', e.message);
      }

      // Schedule next poll (setTimeout chain — more reliable than setInterval)
      if (this.awaitingPayment) {
        this.scheduleNextPoll();
      }
    }
  }
};
</script>

<style scoped>
.topup-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  border-bottom: none;
}

.modal-header .btn-close {
  filter: brightness(0) invert(1);
}

.modal-title {
  font-weight: 600;
}

/* Current Balance */
.current-balance-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--yt-bg-secondary, #f9fafb);
  border-radius: 12px;
  margin-bottom: 20px;
}

.balance-label {
  color: var(--yt-text-secondary, #6b7280);
  font-size: 14px;
}

.balance-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--yt-text-primary, #1f2937);
}

/* Options Grid */
.topup-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.topup-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background-color: var(--yt-bg-card, #fff);
  border: 2px solid var(--yt-border-color, #e5e7eb);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topup-option:hover {
  border-color: var(--yt-primary, #4F46E5);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.15);
}

.topup-option.has-bonus {
  border-color: #10B981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%);
}

.topup-option.has-bonus:hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.option-amount {
  font-size: 20px;
  font-weight: 700;
  color: var(--yt-text-primary, #1f2937);
}

.option-bonus {
  font-size: 13px;
  color: #10B981;
  font-weight: 600;
  margin-top: 4px;
}

.option-total {
  font-size: 14px;
  color: var(--yt-text-secondary, #6b7280);
  margin-top: 2px;
}

/* Payment states */
.payment-awaiting {
  font-size: 14px;
  color: var(--yt-text-secondary, #6b7280);
  text-align: center;
  padding: 14px;
  background-color: var(--yt-bg-secondary, #f9fafb);
  border-radius: 8px;
}

.payment-success {
  font-size: 15px;
  font-weight: 600;
  color: #10B981;
  text-align: center;
  padding: 14px;
  background-color: rgba(16, 185, 129, 0.1);
  border-radius: 8px;
}

/* Info */
.topup-info {
  font-size: 13px;
  color: var(--yt-text-muted, #9ca3af);
  text-align: center;
  padding: 12px;
  background-color: var(--yt-bg-secondary, #f9fafb);
  border-radius: 8px;
}

/* Dark mode */
html[data-bs-theme="dark"] .topup-modal {
  background-color: var(--yt-bg-card, #1f2937);
  border: 1px solid var(--yt-border-color, #374151);
}

html[data-bs-theme="dark"] .modal-body {
  background-color: var(--yt-bg-card, #1f2937);
}

html[data-bs-theme="dark"] .current-balance-card,
html[data-bs-theme="dark"] .topup-info {
  background-color: var(--yt-bg-secondary, #374151);
}

html[data-bs-theme="dark"] .topup-option {
  background-color: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4B5563);
}

html[data-bs-theme="dark"] .topup-option:hover {
  border-color: var(--yt-primary, #6366F1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

html[data-bs-theme="dark"] .topup-option.has-bonus {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
  border-color: #10B981;
}

html[data-bs-theme="dark"] .balance-value,
html[data-bs-theme="dark"] .option-amount {
  color: var(--yt-text-primary, #F9FAFB);
}

html[data-bs-theme="dark"] .balance-label {
  color: var(--yt-text-muted, #9CA3AF);
}

/* Responsive */
@media (max-width: 480px) {
  .topup-options {
    grid-template-columns: 1fr;
  }
}
</style>
